// Deck Stats Panel — live radar chart + progress bars showing aggregate
// values for a deck. Feeds off the `components` list (with attack/defense/
// stamina/dash/burst_resistance/weight fields) and the current `beys`
// selection state from the Deck Builder.

import { useMemo } from "react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { t } from "../lib/i18n";

const STAT_KEYS = ["attack", "defense", "stamina", "dash", "burst_resistance"];

const STAT_LABELS = () => ({
  attack: t.statAttack,
  defense: t.statDefense,
  stamina: t.statStamina,
  dash: t.statDash,
  burst_resistance: t.statBurst,
});

const STAT_COLORS = {
  attack: "#FF0055",
  defense: "#00E5FF",
  stamina: "#E2FF00",
  dash: "#9B7AFF",
  burst_resistance: "#FF9B00",
};

// Slot keys that reference component ids on each bey
const SLOT_TO_TYPE = {
  blade: "blade",
  lockchip: "lockchip",
  over_blade: "overblade",
  assist_blade: "assist_blade",
  ratchet: "ratchet",
  bit: "bit",
};

function computeDeckStats(beys, componentById) {
  const totals = { attack: 0, defense: 0, stamina: 0, dash: 0, burst_resistance: 0 };
  // Per-stat counted parts (skip null values so legacy parts don't dilute anything)
  const perStatCount = { attack: 0, defense: 0, stamina: 0, dash: 0, burst_resistance: 0 };
  let weightTotal = 0;
  let partsCounted = 0;
  const spinCounts = {};
  const seriesCounts = {};

  const totalSlots = beys.length * Object.keys(SLOT_TO_TYPE).length;

  for (const bey of beys) {
    for (const slotKey of Object.keys(SLOT_TO_TYPE)) {
      const id = bey[slotKey];
      if (!id) continue;
      const c = componentById[id];
      if (!c) continue;
      partsCounted += 1;
      for (const k of STAT_KEYS) {
        if (typeof c[k] === "number") {
          totals[k] += c[k];
          perStatCount[k] += 1;
        }
      }
      if (typeof c.weight === "number") weightTotal += c.weight;
      if (c.spin_direction) spinCounts[c.spin_direction] = (spinCounts[c.spin_direction] || 0) + 1;
      if (c.series) seriesCounts[c.series] = (seriesCounts[c.series] || 0) + 1;
    }
  }

  // Per-stat *average* (excludes null values so legacy parts don't drag it down).
  const avg = {};
  // Radar value: totals normalized against full-deck capacity so the plotted
  // area grows monotonically as more parts are added.
  const radar = {};
  for (const k of STAT_KEYS) {
    avg[k] = perStatCount[k] ? Math.round(totals[k] / perStatCount[k]) : 0;
    radar[k] = totalSlots ? Math.round(totals[k] / totalSlots) : 0;
  }
  const dominantSpin =
    Object.entries(spinCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";
  const dominantSeries =
    Object.entries(seriesCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "—";

  return {
    totals, avg, radar,
    weightTotal: Math.round(weightTotal * 10) / 10,
    partsCounted,
    dominantSpin,
    dominantSeries,
  };
}

export default function DeckStatsPanel({ beys, components }) {
  const componentById = useMemo(() => {
    const map = {};
    for (const c of components) map[c.id] = c;
    return map;
  }, [components]);

  const stats = useMemo(
    () => computeDeckStats(beys, componentById),
    [beys, componentById],
  );

  const labels = STAT_LABELS();
  // Radar visualises "capacity filled" so the shape grows as parts are added
  // (totals divided by full-deck slot count).
  const radarData = STAT_KEYS.map((k) => ({
    stat: labels[k],
    value: stats.radar[k],
    key: k,
  }));

  const empty = stats.partsCounted === 0;

  return (
    <div
      data-testid="deck-stats-panel"
      className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-5 lg:p-6 shadow-[0_0_40px_-15px_rgba(255,0,85,0.35)]"
      style={{
        backgroundImage:
          "linear-gradient(135deg, rgba(255,0,85,0.06) 0%, rgba(0,229,255,0.05) 50%, rgba(226,255,0,0.04) 100%)",
      }}
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF] font-mono">
            // {t.radarTitle}
          </div>
          <h3 className="font-display font-black uppercase italic text-2xl mt-1">
            {t.deckStats}
          </h3>
        </div>
        <span
          data-testid="deck-stats-parts-count"
          className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50"
        >
          {stats.partsCounted} {t.partsSelected}
        </span>
      </div>

      <div className="w-full h-[240px] lg:h-[260px] min-w-[240px]">
        <ResponsiveContainer width="99%" height="100%" minWidth={240} minHeight={220}>
          <RadarChart data={radarData} outerRadius="70%">
            <PolarGrid stroke="rgba(255,255,255,0.15)" />
            <PolarAngleAxis
              dataKey="stat"
              tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 10, fontFamily: "monospace" }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 9 }}
              axisLine={false}
            />
            <Radar
              name="Deck"
              dataKey="value"
              stroke="#FF0055"
              fill="#FF0055"
              fillOpacity={0.35}
              strokeWidth={2}
              isAnimationActive
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 space-y-2.5">
        {STAT_KEYS.map((k) => (
          <StatBar
            key={k}
            label={labels[k]}
            value={stats.avg[k]}
            total={stats.totals[k]}
            color={STAT_COLORS[k]}
            testid={`deck-stat-${k}`}
          />
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-3 gap-3 text-center">
        <MetaCell
          label={t.statWeight}
          value={`${stats.weightTotal || 0}g`}
          testid="deck-meta-weight"
        />
        <MetaCell
          label={t.statSpin}
          value={stats.dominantSpin}
          testid="deck-meta-spin"
        />
        <MetaCell
          label={t.statSeries}
          value={stats.dominantSeries}
          testid="deck-meta-series"
        />
      </div>

      {empty && (
        <p
          data-testid="deck-stats-empty"
          className="mt-4 text-[11px] uppercase tracking-[0.2em] text-white/40 text-center font-mono"
        >
          {t.emptyDeckHint}
        </p>
      )}
    </div>
  );
}

function StatBar({ label, value, total, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold font-mono" style={{ color }}>
          {label}
        </span>
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/60">
          <span className="text-white font-bold">{value}</span>
          <span className="text-white/30"> / 100 · Σ {total}</span>
        </span>
      </div>
      <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${Math.min(100, value)}%`,
            background: `linear-gradient(90deg, ${color}, ${color}CC)`,
            boxShadow: `0 0 10px ${color}66`,
          }}
        />
      </div>
    </div>
  );
}

function MetaCell({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.25em] font-bold text-white/40 font-mono">
        {label}
      </div>
      <div className="text-sm font-display font-bold uppercase mt-1 text-white">
        {value}
      </div>
    </div>
  );
}
